import {
    TextField,
    TextFieldProps,
    Popper,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';

interface Variable {
    template: string;
    name: string;
    description: string;
}

function parseVariables(description: string): Variable[] {
    const vars: Variable[] = [];
    const regex = /\$\{([^}]+)\}(?:\s*-\s*(.+))?/g;
    let match;
    while ((match = regex.exec(description)) !== null) {
        vars.push({
            name: match[1],
            template: `\${${match[1]}}`,
            description: (match[2] ?? '').trim(),
        });
    }
    return vars;
}

function getPartialVarAtCursor(
    text: string,
    cursor: number,
): { start: number; partial: string } | null {
    const before = text.slice(0, cursor);
    const idx = before.lastIndexOf('${');
    if (idx === -1) return null;
    const after = before.slice(idx + 2);
    if (after.includes('}')) return null;
    return { start: idx, partial: after };
}

interface VariableAutocompleteTextFieldProps
    extends Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur'> {
    value: string;
    onValueChange: (value: string) => void;
    onBlur?: () => void;
    description?: string;
}

function VariableAutocompleteTextField({
    value,
    onValueChange,
    onBlur,
    description,
    onKeyDownCapture,
    ...textFieldProps
}: VariableAutocompleteTextFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [filteredVars, setFilteredVars] = useState<Variable[]>([]);

    const allVariables = useMemo(() => parseVariables(description ?? ''), [description]);

    const updateDropdown = useCallback(
        (newValue: string, cursor: number, target: HTMLElement) => {
            if (allVariables.length === 0) {
                setAnchorEl(null);
                return;
            }
            const partial = getPartialVarAtCursor(newValue, cursor);
            if (partial !== null) {
                const filtered = allVariables.filter(
                    (v) =>
                        partial.partial === '' ||
                        v.name.toLowerCase().includes(partial.partial.toLowerCase()),
                );
                if (filtered.length > 0) {
                    setFilteredVars(filtered);
                    setSelectedIndex(0);
                    setAnchorEl(target);
                    return;
                }
            }
            setAnchorEl(null);
        },
        [allVariables],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newValue = e.target.value;
            const cursor = e.target.selectionStart ?? newValue.length;
            onValueChange(newValue);
            updateDropdown(newValue, cursor, e.target);
        },
        [onValueChange, updateDropdown],
    );

    const selectVariable = useCallback(
        (variable: Variable) => {
            const input = inputRef.current;
            if (!input) return;
            const cursor = input.selectionStart ?? value.length;
            const partial = getPartialVarAtCursor(value, cursor);
            if (!partial) return;

            const newValue =
                value.slice(0, partial.start) + variable.template + value.slice(cursor);
            onValueChange(newValue);
            setAnchorEl(null);

            const newCursor = partial.start + variable.template.length;
            setTimeout(() => {
                input.focus();
                input.setSelectionRange(newCursor, newCursor);
            }, 0);
        },
        [value, onValueChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (!anchorEl) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => Math.min(i + 1, filteredVars.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && filteredVars.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                selectVariable(filteredVars[selectedIndex]);
            } else if (e.key === 'Escape') {
                setAnchorEl(null);
            }
        },
        [anchorEl, filteredVars, selectedIndex, selectVariable],
    );

    const handleKeyDownCapture = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            // Let autocomplete handle Enter/Escape/arrows when open
            if (
                anchorEl &&
                (e.key === 'Enter' ||
                    e.key === 'Escape' ||
                    e.key === 'ArrowDown' ||
                    e.key === 'ArrowUp')
            ) {
                e.stopPropagation();
            }
            onKeyDownCapture?.(e);
        },
        [anchorEl, onKeyDownCapture],
    );

    const handleBlur = useCallback(() => {
        // Delay so onMouseDown on list items fires before blur closes the list
        setTimeout(() => setAnchorEl(null), 150);
        onBlur?.();
    }, [onBlur]);

    return (
        <>
            <TextField
                {...textFieldProps}
                inputRef={inputRef}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onKeyDownCapture={handleKeyDownCapture}
            />
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            >
                <Paper elevation={4} sx={{ maxHeight: 300, overflowY: 'auto', minWidth: 260 }}>
                    <List dense disablePadding>
                        {filteredVars.map((v, i) => (
                            <ListItem key={v.name} disablePadding>
                                <ListItemButton
                                    selected={i === selectedIndex}
                                    // onMouseDown prevents blur from firing before we can insert
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        selectVariable(v);
                                    }}
                                >
                                    <ListItemText
                                        primary={v.template}
                                        secondary={v.description || undefined}
                                        primaryTypographyProps={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                        }}
                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Popper>
        </>
    );
}

export default VariableAutocompleteTextField;
